import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import OrderModal from './OrderModal';
import { getOrderWechatQrcode, getOrderAlipayQrcode, getOrderStatus } from '../api/mall';
import { QR_CODE_CONFIG, calculateRetryDelay, isRetriableError } from '../config/qrcode';

// 全局支付上下文
// 【微信与支付宝无关且不一致】弹窗内仅共用 UI 壳；微信 = Native 二维码 URL，支付宝 = 前置页 iframe。
// 禁止把微信拉取/缓存/重试逻辑套到支付宝，也禁止用支付宝 iframe 流程改写微信。
const PaymentContext = createContext();

// 支付宝前置页缓存项（非微信 code_url）
class AlipayQrCacheItem {
  constructor(promise, qrCode, expireAt) {
    this.promise = promise;
    this.qrCode = qrCode;
    this.expireAt = expireAt;
    this.abortController = new AbortController();
  }

  isExpired() {
    // 安全边际：提前5秒视为过期
    return Date.now() >= this.expireAt - 5000;
  }

  cancel() {
    this.abortController.abort();
  }
}

// 全局支付管理组件
export const GlobalPaymentManager = ({ children }) => {
  // 统一的支付弹窗状态
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [qrcodeUrls, setQrcodeUrls] = useState({});

  // 统一二维码更新日志序号与日志函数
  const qrcodeUpdateSeqRef = useRef(0);
  const logQrcodeUpdate = useCallback((source, reason, prev, next) => {
    const seq = ++qrcodeUpdateSeqRef.current;
    try {
      console.log(`🧾 [QrcodeUpdate#${seq}] ${source} - ${reason}`, {
        prev,
        next,
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.log(`🧾 [QrcodeUpdate#${seq}] ${source} - ${reason}`);
    }
  }, []);

  // 页面刷新后恢复支付状态
  useEffect(() => {
    const handlePageRefresh = () => {
      // 页面刷新后，清除所有支付状态，避免undefined错误
      if (window.performance && window.performance.navigation.type === 1) {
        console.log('🔄 [PageRefresh] 检测到页面刷新，清除支付状态');
        setOrderModalOpen(false);
        setCurrentOrder(null);
        setQrcodeUrl('');
        setQrcodeUrls(prev => {
          const next = {};
          logQrcodeUpdate('system', 'page refresh reset', prev, next);
          return next;
        });
        modalOpenRef.current = false;
        currentOrderIdRef.current = null;
      }
    };

    // 监听页面加载事件
    window.addEventListener('load', handlePageRefresh);

    return () => {
      window.removeEventListener('load', handlePageRefresh);
    };
  }, []);

  // 支付宝二维码缓存：Map<orderId, AlipayQrCacheItem>
  const alipayQrCache = useRef(new Map());

  // 弹窗状态引用，防止竞态条件
  const modalOpenRef = useRef(false);
  const currentOrderIdRef = useRef(null);

  // 严格模式/并发场景下的重复写入保护（以最新生效值为准）
  const lastWechatUrlRef = useRef('');
  const lastAlipayIframeUrlRef = useRef('');
  const lastAlipayModeRef = useRef('');

  // 调试函数：检查状态一致性
  const debugStateConsistency = useCallback((context = '', expectedOrderId = null) => {
    const state = {
      orderModalOpen,
      currentOrderId: currentOrder?.id,
      modalOpenRef: modalOpenRef.current,
      currentOrderIdRef: currentOrderIdRef.current,
      expectedOrderId,
      timestamp: new Date().toISOString()
    };

    // null / undefined 统一为空，避免关闭态被误判为不一致 → 无意义重渲染
    const normId = (v) => (v == null || v === '' ? null : v);
    const orderIdToCheck = normId(expectedOrderId != null ? expectedOrderId : currentOrder?.id);
    const isConsistent = (orderModalOpen === modalOpenRef.current)
      && (orderIdToCheck === normId(currentOrderIdRef.current));

    return { isConsistent, currentState: state };
  }, [orderModalOpen, currentOrder?.id]);

  // 打开支付弹窗
  const openPaymentModal = useCallback((order) => {
    console.log('🔍 [OpenPaymentModal] 接收到的order参数:', order);
    console.log('🔍 [OpenPaymentModal] order类型:', typeof order);
    console.log('🔍 [OpenPaymentModal] order是否为数组:', Array.isArray(order));

    // 验证order参数
    if (!order) {
      console.error('❌ [OpenPaymentModal] order参数为空');
      return;
    }

    if (Array.isArray(order)) {
      console.error('❌ [OpenPaymentModal] order参数是数组，应该是对象');
      return;
    }

    if (!order.id) {
      console.error('❌ [OpenPaymentModal] order缺少id字段');
      return;
    }

    // 先更新ref，确保状态一致性
    modalOpenRef.current = true;
    currentOrderIdRef.current = order.id;

    // 然后更新state
    setCurrentOrder(order);
    setOrderModalOpen(true);

    console.log('✅ [OpenPaymentModal] 状态更新完成 - orderId:', order.id);

  }, [debugStateConsistency]);

  // 监控状态变化，确保一致性
  useEffect(() => {
    const { isConsistent, currentState } = debugStateConsistency('useEffect-monitor');
    if (isConsistent) return;

    // 只在开发环境显示详细日志
    if (import.meta.env.DEV) {
      console.debug('🔄 [StateMonitor] 检测到状态不一致，尝试同步:', currentState);
    }

    // 同步 modalOpenRef 和 orderModalOpen（仅当确实不同时写 state，避免死循环）
    if (modalOpenRef.current !== orderModalOpen) {
      if (import.meta.env.DEV) {
        console.debug('🔄 [StateSync] 同步 orderModalOpen 状态');
      }
      setOrderModalOpen(modalOpenRef.current);
    }
  }, [orderModalOpen, currentOrder, debugStateConsistency]);

  // 关闭支付弹窗
  const closePaymentModal = useCallback((isPaymentSuccess = false) => {

    // 取消正在进行的支付宝二维码请求
    const currentOrderId = currentOrderIdRef.current;
    if (currentOrderId) {
      const cacheItem = alipayQrCache.current.get(currentOrderId);
      if (cacheItem && cacheItem.abortController) {
        cacheItem.cancel();
      }

      // 清理缓存
      alipayQrCache.current.delete(currentOrderId);
    }

    // 更新状态引用
    modalOpenRef.current = false;
    currentOrderIdRef.current = null;

    setOrderModalOpen(false);
    setCurrentOrder(null);
    setQrcodeUrls(prev => {
      const next = { wechat: '', alipay: '' };
      logQrcodeUpdate('system', 'closePaymentModal reset', prev, next);
      return next;
    });
    setQrcodeUrl('');

    // 🟩 如果是支付成功关闭，触发购物车清空事件
    if (isPaymentSuccess) {
      // 触发自定义事件，通知购物车组件清空购物车
      window.dispatchEvent(new CustomEvent('paymentSuccess', {
        detail: { orderId: currentOrderId }
      }));
    }
  }, []);

  // 获取支付宝支付iframe（唯一入口，SingleFlight实现）
  const getAlipayIframe = useCallback(async (orderIdArg) => {
    // 兜底 orderId 逻辑
    const orderId = orderIdArg ?? currentOrderIdRef.current ?? currentOrder?.id ?? null;

    // 统一的状态检查：检查弹窗状态和orderId匹配
    if (!orderId || !modalOpenRef.current || currentOrderIdRef.current !== orderId) {
      return; // 静默丢弃，不记为失败
    }

    // 检查缓存中是否有未过期的二维码
    const cachedItem = alipayQrCache.current.get(orderId);
    if (cachedItem && cachedItem.qrCode && cachedItem.expireAt > Date.now()) {
      return;
    }

    // 检查是否有正在进行的请求
    if (cachedItem && cachedItem.promise) {
      try {
        const result = await cachedItem.promise;
        // 检查orderId是否仍然匹配
        if (currentOrderIdRef.current === orderId) {
          // 回写结果
          if (result.success && result.qrCode) {
            setQrcodeUrls(prev => {
              const next = { ...prev, alipay: result.qrCode };
              logQrcodeUpdate('alipay', 'qrCode fetched', prev, next);
              return next;
            });
            if (!qrcodeUrls.wechat) {
              setQrcodeUrl(result.qrCode);
            }
          }
        } else {
          // orderId已变更，丢弃结果
        }
      } catch (error) {
        console.error('GlobalPaymentManager - 复用请求失败:', error);
      }
      return;
    }

    // 创建新的请求

    // 创建AbortController用于取消请求
    const abortController = new AbortController();

    // 创建请求Promise
    const requestPromise = (async () => {
      const maxRetries = QR_CODE_CONFIG.MAX_RETRIES;
      let retryCount = 0;

      while (retryCount < maxRetries) {
        try {
          // 检查是否被取消
          if (abortController.signal.aborted) {
            throw new Error('请求已取消');
          }

          // 检查orderId是否仍然匹配
          if (currentOrderIdRef.current !== orderId) {
            throw new Error('orderId已变更');
          }

          const qrRes = await getOrderAlipayQrcode(orderId);

          // 统一响应取值路径
          const payload = qrRes?.data?.data ?? qrRes?.data;

          // 检查是否为前置模式
          if (payload?.alipayMode === 'PAGE_PAY_IFRAME' && payload?.alipayUrl) {

            // 设置状态，供 OrderModal 使用
            setQrcodeUrls(prev => {
              // 二次保护：若本次要写入的与“最近一次已应用值”一致，则直接跳过
              if (lastAlipayModeRef.current === 'PAGE_PAY_IFRAME' && lastAlipayIframeUrlRef.current === payload.alipayUrl) {
                console.log('🔁 [QrcodeUpdate] skip duplicate (alipay prepared)');
                return prev;
              }
              const next = {
                ...prev,
                alipayMode: 'PAGE_PAY_IFRAME',
                alipayUrl: payload.alipayUrl
              };
              // 更新最近一次已应用值
              lastAlipayModeRef.current = 'PAGE_PAY_IFRAME';
              lastAlipayIframeUrlRef.current = payload.alipayUrl;
              logQrcodeUpdate('alipay', 'page pay iframe prepared', prev, next);
              return next;
            });

            return {
              success: true,
              alipayMode: 'PAGE_PAY_IFRAME',
              alipayUrl: payload.alipayUrl,
              outTradeNo: payload.outTradeNo,
              expireAt: payload.expireAt ? new Date(payload.expireAt).getTime() : Date.now() + 60000,
              serverNow: payload.serverNow ? new Date(payload.serverNow).getTime() : Date.now()
            };
          }

          // 检查二维码是否为空（仅对当面付模式）
          const qr = payload?.qrCode;
          if (!qr) {
            console.error('GlobalPaymentManager - 响应中二维码为空:', payload);
            return { success: false, error: '响应中二维码为空', code: 'RESPONSE_NO_QRCODE' };
          }

          // 检查orderId是否仍然匹配
          if (currentOrderIdRef.current === orderId) {

            // 计算过期时间 - 使用服务器时间作为基准
            const serverNow = payload.serverNow ? new Date(payload.serverNow) : new Date();
            const expireAt = payload.expireAt ? new Date(payload.expireAt) : new Date(Date.now() + 60000);

            return {
              success: true,
              qrCode: qr,
              expireAt: expireAt.getTime(),
              serverNow: serverNow.getTime()
            };
          } else {
            throw new Error('orderId已变更，丢弃结果');
          }

        } catch (error) {
          console.error(`GlobalPaymentManager - 支付宝二维码请求失败 (第${retryCount + 1}次):`, error);

          // 检查是否被取消或orderId变更
          if (error.message === '请求已取消' || error.message === 'orderId已变更') {
            throw error;
          }

          // 检查HTTP状态码，按状态码分流处理
          const status = error.response?.status;
          const message = error.message || '';

          // 根据HTTP状态码决定是否重试
          switch (status) {
            case 200:
              // 成功，不需要重试
              return { success: true, qrCode: error.response.data.qrCode };

            case 410:
              // 二维码接近过期，触发一次刷新（不重试）
              console.log('GlobalPaymentManager - 二维码接近过期，触发刷新');
              return { success: false, error: '二维码即将过期，建议刷新', code: 'QRCODE_NEAR_EXPIRE' };

            case 409:
              // 订单状态不允许支付，停止重试
              console.warn('GlobalPaymentManager - 订单状态不允许支付，停止重试');
              return { success: false, error: '当前订单不可支付/已有进行中支付', code: 'ORDER_STATE_INVALID' };

            case 404:
              // 订单不存在，停止重试
              console.warn('GlobalPaymentManager - 订单不存在，停止重试');
              return { success: false, error: '订单不存在或无权限', code: 'ORDER_NOT_FOUND' };

            case 422:
              // 参数错误，停止重试
              console.warn('GlobalPaymentManager - 请求参数错误，停止重试:', error.response.data.message);
              return { success: false, error: error.response.data.message, code: 'INVALID_PARAM' };

            case 502:
            case 503:
            case 504:
              // 网关错误，可重试
              console.log(`GlobalPaymentManager - 网关错误 (${status})，可重试`);
              break;

            default:
              // 其他错误，检查是否可重试
              if (isRetriableError(status, message)) {
                console.log(`GlobalPaymentManager - 可重试错误 (${status})`);
                break;
              } else {
                console.warn(`GlobalPaymentManager - 不可重试错误 (${status})，停止重试`);
                return { success: false, error: message, code: 'UNKNOWN_ERROR' };
              }
          }

          // 可重试的错误，继续重试逻辑
          retryCount++;
          if (retryCount < maxRetries) {
            const delay = calculateRetryDelay(retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      return { success: false, error: '已达到最大重试次数' };
    })();

    // 创建缓存项
    const cacheItem = new AlipayQrCacheItem(requestPromise, null, Date.now() + 120000); // 2分钟过期
    cacheItem.abortController = abortController;

    // 存储到缓存
    alipayQrCache.current.set(orderId, cacheItem);

    try {
      const result = await requestPromise;

      // 检查orderId是否仍然匹配
      if (currentOrderIdRef.current === orderId) {
        if (result.success) {

          // 处理前置模式
          if (result.type === 'PAGE_PAY_QRCODE' && result.mode === 'PAGE_PAY_IFRAME') {

            // 只在弹窗仍然打开且orderId匹配时回写状态
            if (!modalOpenRef.current || currentOrderIdRef.current !== orderId) {
              return;
            }

            // 设置前置模式相关状态
            setQrcodeUrls(prev => {
              // 二次保护：若本次要写入的与“最近一次已应用值”一致，则直接跳过
              if (lastAlipayModeRef.current === 'PAGE_PAY_IFRAME' && lastAlipayIframeUrlRef.current === result.alipayUrl) {
                console.log('🔁 [QrcodeUpdate] skip duplicate (alipay result)');
                return prev;
              }
              const next = {
                ...prev,
                alipay: 'PAGE_PAY_IFRAME',
                alipayMode: 'PAGE_PAY_IFRAME',
                alipayUrl: result.alipayUrl
              };
              // 更新最近一次已应用值
              lastAlipayModeRef.current = 'PAGE_PAY_IFRAME';
              lastAlipayIframeUrlRef.current = result.alipayUrl;
              logQrcodeUpdate('alipay', 'page pay iframe result applied', prev, next);
              return next;
            });

            return;
          }

          // 处理当面付模式（原有逻辑）
          if (result.qrCode) {

            // 只在弹窗仍然打开且orderId匹配时回写状态
            if (!modalOpenRef.current || currentOrderIdRef.current !== orderId) {
              return; // 静默丢弃，不记为失败
            }

            // 更新缓存项
            cacheItem.qrCode = result.qrCode;
            cacheItem.expireAt = result.expireAt;

            // 检查是否接近过期（≤10分钟），如果是则主动刷新
            const now = Date.now();
            const timeLeft = result.expireAt - now;

            if (timeLeft <= 600000) { // ≤10分钟，到期前10分钟才允许重生码
              // 触发一次刷新，获取新二维码
              setTimeout(() => {
                if (currentOrderIdRef.current === orderId && modalOpenRef.current) {
                  getAlipayIframe(orderId);
                }
              }, 300000); // 5分钟后刷新，给用户充足时间扫码
            }

            setQrcodeUrls(prev => {
              // 避免不必要的更新，只有当实际内容变化时才更新
              if (prev.alipay === result.qrCode) {
                return prev;
              }

              const next = { ...prev, alipay: result.qrCode };
              logQrcodeUpdate('alipay', 'qrCode applied', prev, next);
              return next;
            });

            if (!qrcodeUrls.wechat) {
              setQrcodeUrl(result.qrCode);
            }
          }
        } else {
          // 只在弹窗仍然打开且orderId匹配时显示错误
          if (modalOpenRef.current && currentOrderIdRef.current === orderId) {
            console.error('GlobalPaymentManager - 支付宝二维码获取失败:', result.error, {
              orderId,
              modalState: {
                isOpen: modalOpenRef.current,
                currentOrderId: currentOrderIdRef.current
              },
              error: {
                message: result.error,
                code: result.code,
                timestamp: new Date().toISOString()
              }
            });
            setQrcodeUrls(prev => {
              // 避免不必要的更新，只有当实际内容变化时才更新
              if (prev.alipay === 'ERROR') {
                return prev;
              }
              const next = { ...prev, alipay: 'ERROR' };
              logQrcodeUpdate('alipay', 'error status applied', prev, next);
              return next;
            });
          } else {
            // 弹窗已关闭或orderId已变更，静默丢弃错误结果
          }
        }
      } else {
        // orderId已变更，丢弃结果
      }

    } catch (error) {
      console.error('GlobalPaymentManager - 支付宝二维码请求异常:', error);

      // 检查orderId是否仍然匹配
      if (currentOrderIdRef.current === orderId) {
        setQrcodeUrls(prev => {
          // 避免不必要的更新，只有当实际内容变化时才更新
          if (prev.alipay === 'ERROR') {
            return prev;
          }
          const next = { ...prev, alipay: 'ERROR' };
          logQrcodeUpdate('alipay', 'exception status applied', prev, next);
          return next;
        });
      }
    } finally {
      // 清理缓存（保留成功的缓存项）
      if (!cacheItem.qrCode) {
        alipayQrCache.current.delete(orderId);
      }
    }
  }, []); // 移除qrcodeUrls.wechat依赖，避免不必要的重新执行

  // 监听弹窗状态变化，触发支付宝二维码获取
  useEffect(() => {
    // 检查状态一致性
    const { isConsistent, currentState } = debugStateConsistency('useEffect-trigger');

    // 使用更可靠的状态检查：同时检查state和ref
    const shouldFetch = orderModalOpen &&
      currentOrder?.id &&
      modalOpenRef.current &&
      currentOrderIdRef.current === currentOrder.id;

    if (shouldFetch) {
      // 添加防抖，避免频繁触发
      const timeoutId = setTimeout(() => {
        // 再次检查状态，确保在防抖期间状态没有变化
        const stillValid = modalOpenRef.current &&
          currentOrderIdRef.current === currentOrder.id;

        if (stillValid) {
          getAlipayIframe(); // 不传参，内部会兜底
        } else {
          // 防抖期间状态已变化，跳过获取
        }
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      // 状态检查未通过，跳过支付宝二维码获取
    }
  }, [orderModalOpen, currentOrder?.id, getAlipayIframe, debugStateConsistency]);

  // 防重复调用缓存
  const qrcodeRefreshCache = useRef(new Map());
  const wechatQrcodeCallCache = useRef(new Map());

  // 获取微信支付二维码 - 调用后端API
  const getWechatQrcode = useCallback(async () => {
    console.log('🔍 [WechatQRCode] 函数被调用 - 调用栈:', new Error().stack);
    console.log('🔍 [WechatQRCode] 当前时间:', new Date().toISOString());
    console.log('🔍 [WechatQRCode] 当前状态:', {
      orderModalOpen,
      currentOrder: currentOrder ? { id: currentOrder.id, userId: currentOrder.userId } : null,
      currentOrderIdRef: currentOrderIdRef.current,
      modalOpenRef: modalOpenRef.current
    });

    // 使用 ref 获取当前订单，避免依赖 currentOrder state
    const orderId = currentOrderIdRef.current;
    if (!orderId) {
      console.warn('⚠️ [WechatQRCode] 没有当前订单ID，跳过微信二维码获取');
      return;
    }

    // 从 state 获取订单信息，确保获取到最新的订单数据
    const order = currentOrder;
    if (!order) {
      console.warn('⚠️ [WechatQRCode] 没有当前订单对象，跳过微信二维码获取');
      console.warn('⚠️ [WechatQRCode] 调试信息:', {
        orderId,
        currentOrder,
        orderModalOpen,
        modalOpenRef: modalOpenRef.current,
        currentOrderIdRef: currentOrderIdRef.current
      });
      return;
    }

    const now = Date.now();
    const lastCallTime = wechatQrcodeCallCache.current.get(orderId) || 0;
    const timeSinceLastCall = now - lastCallTime;

    console.log('🔍 [WechatQRCode] 调用频率检查:', {
      orderId,
      now,
      lastCallTime,
      timeSinceLastCall,
      shouldSkip: timeSinceLastCall < 5000
    });

    // 5秒内同一订单只调用一次
    if (timeSinceLastCall < 5000) {
      console.log('🔄 [WechatQRCode] 微信二维码调用过于频繁，跳过本次调用 - orderId:', orderId, '距离上次调用:', timeSinceLastCall, 'ms');
      return;
    }

    console.log('📱 [WechatQRCode] 开始调用后端API获取微信支付二维码 - orderId:', orderId);
    console.log('📱 [WechatQRCode] 订单信息:', {
      id: order.id,
      userId: order.userId,
      orderNumber: order.orderNumber || 'N/A'
    });

    wechatQrcodeCallCache.current.set(orderId, now);

    try {
      // 调用后端API获取微信支付二维码
      console.log('🌐 [WechatQRCode] 调用后端API - getOrderWechatQrcode');
      const wechatRes = await getOrderWechatQrcode(order.id, order.userId);
      console.log('🌐 [WechatQRCode] 后端API响应:', wechatRes);

      if (wechatRes.data?.success) {
        const wechatData = wechatRes.data.data;
        console.log('🌐 [WechatQRCode] 微信数据:', wechatData);

        if (wechatData?.qrcodeUrl) {
          console.log('🌐 [WechatQRCode] 设置微信二维码URL:', wechatData.qrcodeUrl);
          setQrcodeUrls(prev => {
            // 二次保护：若与最近一次已应用的微信URL相同，跳过
            if (lastWechatUrlRef.current === wechatData.qrcodeUrl) {
              console.log('🔁 [QrcodeUpdate] skip duplicate (wechat url)');
              return prev;
            }
            const next = {
              ...prev,
              wechat: wechatData.qrcodeUrl
            };
            // 更新最近一次已应用值
            lastWechatUrlRef.current = wechatData.qrcodeUrl;
            logQrcodeUpdate('wechat', 'qrcodeUrl fetched', prev, next);
            return next;
          });
          setQrcodeUrl(wechatData.qrcodeUrl);
          console.log('✅ [WechatQRCode] 微信二维码获取成功');
        } else {
          console.warn('⚠️ [WechatQRCode] 微信数据中没有qrcodeUrl:', wechatData);
        }
      } else {
        console.warn('❌ [WechatQRCode] 微信二维码获取失败:', wechatRes.data?.message);
      }
    } catch (wechatError) {
      console.warn('❌ [WechatQRCode] 微信二维码获取异常:', wechatError);
    }
  }, [currentOrder, orderModalOpen]); // 添加更多依赖，确保获取到最新状态

  // 刷新支付二维码
  const refreshPaymentQrcode = useCallback(async () => {
    if (!currentOrder) {
      console.warn('⚠️ [QRCodeRefresh] 没有当前订单，跳过二维码刷新');
      return;
    }

    // 检查currentOrder是否为有效对象
    if (Array.isArray(currentOrder)) {
      console.error('❌ [QRCodeRefresh] currentOrder是数组，应该是对象');
      return;
    }

    if (!currentOrder.id) {
      console.error('❌ [QRCodeRefresh] currentOrder缺少id字段');
      return;
    }

    const orderId = currentOrder.id;
    const now = Date.now();
    const lastRefreshTime = qrcodeRefreshCache.current.get(orderId) || 0;

    // 10秒内同一订单只刷新一次
    if (now - lastRefreshTime < 10000) {
      console.log('🔄 [QRCodeRefresh] 二维码刷新过于频繁，跳过本次刷新 - orderId:', orderId);
      return;
    }

    console.log('🔄 [QRCodeRefresh] 开始刷新支付二维码 - orderId:', orderId);
    qrcodeRefreshCache.current.set(orderId, now);

    try {
      // 强制刷新所有二维码，清除旧状态
      console.log('🧹 [QRCodeRefresh] 清除旧的二维码状态');
      setQrcodeUrl('');
      setQrcodeUrls(prev => {
        const next = {};
        logQrcodeUpdate('system', 'manual refresh reset', prev, next);
        return next;
      });

      const newQrcodeUrls = {};

      // 分别获取微信和支付宝二维码，避免重复调用
      console.log('📱 [QRCodeRefresh] 分别获取微信和支付宝二维码');

      // 获取微信二维码
      try {
        console.log('🔍 [QRCodeRefresh] 获取微信二维码');
        const wechatRes = await getOrderWechatQrcode(currentOrder.id, currentOrder.userId);
        if (wechatRes.data?.success) {
          const wechatData = wechatRes.data.data;
          if (wechatData?.qrcodeUrl) {
            newQrcodeUrls.wechat = wechatData.qrcodeUrl;
            setQrcodeUrl(wechatData.qrcodeUrl); // 微信优先
            console.log('✅ [QRCodeRefresh] 微信二维码获取成功');
          }
        } else {
          console.warn('❌ [QRCodeRefresh] 微信二维码获取失败:', wechatRes.data?.message);
        }
      } catch (wechatError) {
        console.warn('❌ [QRCodeRefresh] 微信二维码获取异常:', wechatError);
      }

      // 获取支付宝二维码
      try {
        console.log('🔍 [QRCodeRefresh] 获取支付宝二维码');
        const alipayRes = await getOrderAlipayQrcode(currentOrder.id);
        if (alipayRes.data?.success) {
          const alipayData = alipayRes.data.data;
          if (alipayData?.qrCode) {
            newQrcodeUrls.alipay = alipayData.qrCode;
            // 如果没有微信二维码，设置支付宝为默认
            if (!newQrcodeUrls.wechat) {
              setQrcodeUrl(alipayData.qrCode);
            }
            console.log('✅ [QRCodeRefresh] 支付宝二维码获取成功');
          }
        } else {
          console.warn('❌ [QRCodeRefresh] 支付宝二维码获取失败:', alipayRes.data?.message);
        }
      } catch (alipayError) {
        console.warn('❌ [QRCodeRefresh] 支付宝二维码获取异常:', alipayError);
      }

      setQrcodeUrls(prev => {
        const next = { ...newQrcodeUrls };
        logQrcodeUpdate('system', 'manual refresh apply', prev, next);
        return next;
      });
      console.log('📋 [QRCodeRefresh] 二维码刷新完成 - 微信:', !!newQrcodeUrls.wechat, '支付宝:', !!newQrcodeUrls.alipay);

    } catch (error) {
      console.error('❌ [QRCodeRefresh] 刷新支付二维码失败:', error);
    }
  }, [currentOrder]);

  // 检查支付状态
  const checkPaymentStatus = useCallback(async (orderId) => {
    try {
      console.log('🔍 [PaymentStatus] 开始检查支付状态 - orderId:', orderId);
      const res = await getOrderStatus(orderId);
      const status = res.data.data?.status?.toLowerCase();
      const result = status === 'paid' ? 'success' : 'pending';
      console.log('📊 [PaymentStatus] 支付状态检查结果 - orderId:', orderId, 'status:', status, 'result:', result);
      return result;
    } catch (error) {
      console.error('❌ [PaymentStatus] 检查支付状态失败 - orderId:', orderId, 'error:', error);
      return 'pending';
    }
  }, []);

  // 支付成功后的回调
  const onPaymentSuccess = useCallback(() => {
    console.log('✅ [PaymentSuccess] 支付成功回调被触发');
    // 支付成功后自动关闭弹窗
    setTimeout(() => {
      console.log('🔄 [PaymentSuccess] 开始关闭支付弹窗');
      closePaymentModal(true); // 传入true表示是支付成功关闭
      console.log('✅ [PaymentSuccess] 支付弹窗已关闭');
      // 可以在这里添加全局的支付成功处理逻辑
      // 比如刷新订单列表、显示成功提示等
    }, 2000);
  }, [closePaymentModal]);

  const value = {
    // 状态
    orderModalOpen,
    currentOrder,
    qrcodeUrl,
    qrcodeUrls,

    // 方法
    openPaymentModal,
    closePaymentModal,
    refreshPaymentQrcode,
    getAlipayIframe,
    checkPaymentStatus,
    onPaymentSuccess,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
      <OrderModal
        open={orderModalOpen}
        order={currentOrder}
        onClose={closePaymentModal}
        onRetry={getWechatQrcode}
        qrcodeUrls={qrcodeUrls}
        onGetWechatQrcode={getWechatQrcode}
        onGetAlipayIframe={getAlipayIframe}
        onPayStatus={checkPaymentStatus}
      />
    </PaymentContext.Provider>
  );
};

// 使用支付上下文的Hook
export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a GlobalPaymentManager');
  }
  return context;
};

export default GlobalPaymentManager;

